<template>
  <div
    id="app"
    class="min-h-screen bg-primary-100 text-secondary-800 antialiased p-4 sm:p-6"
  >
    <!-- INITIALIZING -->
    <div
      v-if="authStore.isInitializing"
      class="grid min-h-[60vh] place-items-center"
    >
      <div
        class="w-full max-w-sm rounded-xl border border-neutral-200 bg-white p-6 shadow-sm"
      >
        <div class="flex items-center gap-3">
          <span
            class="inline-flex h-8 w-8 animate-spin rounded-full border-2 border-primary-500 border-t-transparent"
          ></span>
          <div>
            <p class="text-base font-semibold">Logging in…</p>
            <p class="text-sm text-neutral-500">Please wait.</p>
          </div>
        </div>
      </div>
    </div>

    <!-- ERROR -->
    <div
      v-else-if="authStore.error"
      class="grid min-h-[60vh] place-items-center"
    >
      <div
        class="w-full max-w-md rounded-xl border border-accent-three-300 bg-white p-6 shadow-sm"
      >
        <h2 class="mb-2 text-xl font-semibold">Login failed</h2>
        <p class="mb-6 text-neutral-600">{{ authStore.error }}</p>
        <button
          class="rounded-lg bg-primary-500 px-4 py-2 text-white hover:bg-primary-800 active:bg-primary-800"
          @click="retryLogin"
        >
          Retry
        </button>
      </div>
    </div>

    <!-- AUTHENTICATED -->
    <div v-else-if="authStore.isAuthenticated" class="mx-auto w-full max-w-4xl">
      <!-- Header -->
      <HeaderStatusContainer :initialization-failed="initializationFailed" />

      <!-- AI Assistant Panel -->
      <div class="rounded-xl border border-neutral-200 bg-white shadow-sm">
        <div
          class="border-b border-neutral-100 px-5 py-3 flex justify-between items-center"
        >
          <h2 class="text-lg font-semibold text-secondary-800">Pete</h2>
          <button
            class="px-3 py-1.5 text-sm font-medium text-primary-700 bg-primary-50 border border-primary-200 rounded-lg hover:bg-primary-100 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            :disabled="
              messageStore.customerMessages.length === 0 ||
              messageStore.aiResponses.length < 1 ||
              messageStore.isSummaryGenerating ||
              messageStore.isAiResponseProcessing
            "
            @click="generateSummary"
          >
            <span
              v-if="messageStore.isSummaryGenerating"
              class="inline-flex h-3 w-3 animate-spin rounded-full border border-primary-500 border-t-transparent"
            ></span>
            <span>{{
              messageStore.isSummaryGenerating ? 'Genereren...' : 'Samenvatting'
            }}</span>
          </button>
        </div>

        <div class="p-5">
          <!-- Summary Section -->
          <div
            v-if="
              messageStore.hasSummary ||
              messageStore.isSummaryGenerating ||
              messageStore.summaryError
            "
            class="mb-4"
          >
            <SummaryContainer />
          </div>

          <!-- Processing State -->
          <div
            v-if="messageStore.isAiResponseProcessing"
            class="rounded-lg border-2 border-primary-300 bg-primary-100 p-4 mb-4 shadow-lg"
          >
            <div class="flex items-center gap-3">
              <span
                class="inline-flex h-6 w-6 animate-spin rounded-full border-3 border-primary-500 border-t-transparent"
              ></span>
              <div>
                <p class="font-bold text-primary-800 text-lg">
                  Bericht van klant verwerken...
                </p>
                <p class="text-base text-primary-800 font-medium">
                  {{
                    messageStore.aiResponseProcessing.stage === 'sending'
                      ? 'Versturen naar AI-service...'
                      : messageStore.aiResponseProcessing.stage === 'generating'
                        ? 'Pete genereert respons...'
                        : 'Verwerken...'
                  }}
                </p>
              </div>
            </div>
          </div>

          <!-- Error State -->
          <div
            v-if="messageStore.aiResponseError"
            class="rounded-lg border border-accent-three-300 bg-accent-three-100 p-4 mb-4"
          >
            <div class="flex items-start gap-3">
              <svg
                class="h-5 w-5 text-accent-three-500 mt-0.5 flex-shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clip-rule="evenodd"
                ></path>
              </svg>
              <div>
                <p class="font-medium text-accent-three-800">
                  Error processing message
                </p>
                <p class="text-sm text-accent-three-800 mt-1">
                  {{ messageStore.aiResponseError }}
                </p>
                <button
                  class="mt-2 text-sm text-accent-three-500 hover:text-accent-three-800 underline"
                  @click="messageStore.clearAiResponse()"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>

          <!-- No Activity State -->
          <div
            v-if="
              !messageStore.isAiResponseProcessing &&
              !messageStore.hasAiResponse &&
              !messageStore.aiResponseError
            "
            class="grid place-items-center py-12 text-neutral-500"
          >
            <div class="text-center">
              <svg
                class="mx-auto h-12 w-12 text-neutral-400 mb-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a2 2 0 01-2-2v-6a2 2 0 012-2h8z M5 6a2 2 0 012-2h6a2 2 0 012 2v2H7a2 2 0 00-2 2v6z"
                />
              </svg>
              <p class="text-lg font-medium text-secondary-800 mb-2">
                Wachten op klantberichten
              </p>
              <p class="text-sm text-neutral-500">
                AI-assistentie verschijnt hier wanneer klanten berichten sturen
              </p>
            </div>
          </div>

          <!-- Waiting for Messages State -->
          <div
            v-else-if="
              messageStore.isAiResponseProcessing &&
              messageStore.hasAiResponse &&
              messageStore.aiResponseError &&
              messageStore.customerMessages.length > 0
            "
            class="grid place-items-center py-12 text-neutral-500"
          >
            <div class="text-center">
              <svg
                class="mx-auto h-8 w-8 text-neutral-400 mb-3"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                ></path>
              </svg>
              <p class="text-sm text-neutral-600">
                Ready to assist with new customer messages
              </p>
            </div>
          </div>

          <!-- AI Response -->
          <AiResponseContainer />
        </div>
      </div>
    </div>

    <!-- NOT AUTHENTICATED -->
    <div v-else class="grid min-h-[60vh] place-items-center text-center">
      <div
        class="w-full max-w-sm rounded-xl border border-neutral-200 bg-white p-6 shadow-sm"
      >
        <h2 class="mb-3 text-xl font-semibold text-secondary-800">
          Not authenticated
        </h2>
        <button
          class="rounded-lg bg-primary-500 px-4 py-2 text-white hover:bg-primary-800 active:bg-primary-800"
          @click="login"
        >
          Login
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref, watch, nextTick } from 'vue';
import { useAuthStore } from '@/stores/auth';
import { useMessageStore } from '@/stores/messages';
import { IntegrationCoordinator } from '@/services/IntegrationCoordinator';
import { BackendWebSocketService } from '@/services/BackendWebSocketService';
import HeaderStatusContainer from '@/components/header/HeaderStatusContainer.vue';
import AiResponseContainer from '@/components/ai-response/AiResponseContainer.vue';
import SummaryContainer from '@/components/ai-response/SummaryContainer.vue';

const authStore = useAuthStore();
const messageStore = useMessageStore();

const listEl = ref<HTMLElement | null>(null);
const initializationFailed = ref(false);

onMounted(async () => {
  await authStore.initialize();

  if (authStore.isAuthenticated) {
    const success = await IntegrationCoordinator.initialize();
    if (!success) {
      initializationFailed.value = true;
    }
  }

  nextTick(scrollToBottom);
});

onUnmounted(() => {
  IntegrationCoordinator.cleanup();
});

// Watch for new messages and auto-scroll
watch(
  () => messageStore.messages.length,
  async () => {
    await nextTick();
    scrollToBottom();
  }
);

const login = () => {
  authStore.initialize();
};

const retryLogin = () => {
  authStore.error = null;
  authStore.initialize();
};

const generateSummary = async () => {
  const success = await BackendWebSocketService.sendSummaryRequest();
  if (!success) {
    messageStore.setSummaryError('Kon geen verbinding maken met de service');
  }
};

function scrollToBottom() {
  const el = listEl.value;
  if (!el) return;
  el.scrollTop = el.scrollHeight;
}
</script>
